import { useEffect, useRef, useState } from 'react'
import { deleteNode, previewNodeDeletion } from '@/api/nodes'
import type { NodeSummary } from '@/types'
import styles from './NodeDangerZone.module.css'

interface Props {
  node: NodeSummary
  canDelete: boolean
  onDeleted: () => void
}

export default function NodeDangerZone({ node, canDelete, onDeleted }: Props) {
  const [open, setOpen] = useState(false)
  const deleteButton = useRef<HTMLButtonElement>(null)
  return (
    <section className={styles.card} aria-labelledby="node-danger-zone-title">
      <h2 id="node-danger-zone-title">Danger Zone</h2>
      <p>서비스가 종료된 노드를 삭제하고 연결된 논리 노드와 운영 기능을 정리합니다.</p>
      <button ref={deleteButton} type="button" className={styles.danger} disabled={!canDelete} onClick={() => setOpen(true)}>
        노드 삭제
      </button>
      {!canDelete && <p className={styles.hint}>관리자만 삭제할 수 있습니다.</p>}
      {open && canDelete && (
        <DeleteNodeModal key={node.id} node={node} onClose={() => {
          setOpen(false)
          deleteButton.current?.focus()
        }} onDeleted={onDeleted} />
      )}
    </section>
  )
}

function DeleteNodeModal({ node, onClose, onDeleted }: Omit<Props, 'canDelete'> & { onClose: () => void }) {
  const [count, setCount] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const dialog = useRef<HTMLDivElement>(null)
  const cancel = useRef<HTMLButtonElement>(null)
  const submitting = useRef(false)

  useEffect(() => {
    let cancelled = false
    setCount(null)
    setError(null)
    previewNodeDeletion(node.id).then((preview) => {
      if (!cancelled) setCount(preview.guardian_node_count)
    }).catch((caught: unknown) => {
      if (!cancelled) setError((caught as { message?: string }).message ?? '삭제 대상을 확인하지 못했습니다.')
    })
    return () => { cancelled = true }
  }, [node.id, attempt])

  useEffect(() => { cancel.current?.focus() }, [])

  async function handleDelete() {
    if (count === null || submitting.current) return
    submitting.current = true
    setSaving(true)
    setError(null)
    try {
      await deleteNode(node.id)
      onDeleted()
    } catch (caught: unknown) {
      setError((caught as { message?: string }).message ?? '노드를 삭제하지 못했습니다.')
      submitting.current = false
      setSaving(false)
    }
  }

  return (
    <div className={styles.backdrop}>
      <div ref={dialog} className={styles.modal} role="dialog" aria-modal="true"
        aria-labelledby="delete-node-title" aria-describedby="delete-node-warning"
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !saving) onClose()
          if (event.key === 'Tab') {
            const buttons = dialog.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')
            const first = buttons?.[0]
            const last = buttons?.[buttons.length - 1]
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault(); last?.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault(); first?.focus()
            }
          }
        }}>
        <h2 id="delete-node-title">노드를 삭제하시겠습니까?</h2>
        <dl className={styles.identity}>
          <dt>노드</dt><dd>{node.name || node.node_key}</dd>
          <dt>Node key</dt><dd>{node.node_key}</dd>
          <dt>고객사</dt><dd>{node.company || '미지정'}</dd>
          <dt>현장</dt><dd>{node.site || '미지정'}</dd>
        </dl>
        <div id="delete-node-warning" className={styles.warning}>
          {count === null
            ? <p>{error ? '논리 노드 개수를 확인하지 못했습니다.' : '연결된 논리 노드 개수를 확인하고 있습니다.'}</p>
            : <p>이 물리 노드와 연결된 <strong>논리 노드 {count}개</strong>가 함께 제거됩니다.</p>}
          <p>Slack 알림과 자동 업로드 등 연관 운영 기능이 비활성화되며 기존 에이전트의 재접속이 차단됩니다.</p>
          <p>실제 PC의 실행 중인 작업이나 파일을 종료·삭제하지 않습니다.</p>
        </div>
        {error && <p role="alert">{error}</p>}
        {error && count === null && (
          <button type="button" onClick={() => setAttempt((value) => value + 1)}>다시 확인</button>
        )}
        <div className={styles.actions}>
          <button ref={cancel} type="button" disabled={saving} onClick={onClose}>취소</button>
          <button type="button" className={styles.danger} disabled={saving || count === null} onClick={handleDelete}>
            {saving ? '삭제 중…' : '삭제'}
          </button>
        </div>
      </div>
    </div>
  )
}
